/**
 * Extracts a human-readable message from an Axios error response.
 *
 * Handles express-validator's `{ errors: [{ msg, path }] }` shape AND the
 * service-layer `{ errors: ['Recipient address is required', ...] }` plain-string
 * shape (both HTTP 400) — which the older `data.message || data.error` reads
 * missed, surfacing a generic "Unknown error" instead. Falls back to `message`,
 * then `error`, then a translated fallback key. Backward-compatible with the
 * previous shapes.
 *
 * The backend has no i18n, so its validation strings arrive in English. Pass an
 * optional `messageMap` (raw backend string -> i18n key) to localize known
 * messages; anything unmapped degrades gracefully to the raw string.
 */
export function getApiErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  messageMap?: Record<string, string>
): string {
  const data = error?.response?.data;
  if (data) {
    if (typeof data.code === 'string' && messageMap?.[data.code]) {
      return t(messageMap[data.code]);
    }
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const messages = data.errors
        .map((e: any) => (typeof e === 'string' ? e : e?.msg))
        .filter((m: any): m is string => typeof m === 'string' && m.length > 0)
        .map((m: string) => (messageMap && messageMap[m] ? t(messageMap[m]) : m));
      if (messages.length > 0) return messages.join('; ');
    }
    if (typeof data.message === 'string' && data.message) return data.message;
    if (typeof data.error === 'string' && data.error) return data.error;
  }
  return t(fallbackKey);
}
